import { useState } from "react";
import { Card } from "@tremor/react";
import { cn } from "@/lib/utils";
import {
  useAlerts, useAlertSummary,
  type Severity, type Rule, type FlowAlert,
} from "@/lib/queries";
import { AlertDetailDrawer } from "@/components/AlertDetailDrawer";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-400",
  high:     "bg-orange-500/10 text-orange-600 border-orange-500/20 dark:text-orange-400",
  medium:   "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
  low:      "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
  info:     "bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400",
};

const SEVERITIES: (Severity | "all")[] = ["all", "critical", "high", "medium", "low", "info"];

interface RuleTabDef {
  rule: Rule | "all";
  label: string;
}
const RULE_TABS: RuleTabDef[] = [
  { rule: "all",          label: "All rules" },
  { rule: "port_scan",    label: "Port scans" },
  { rule: "threat_intel", label: "Threat intel" },
  { rule: "geo_anomaly",  label: "Geo anomalies" },
  { rule: "beacon",       label: "Beacons" },
];

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function FilterPill({
  label, active, onClick,
}: {
  label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {label}
    </button>
  );
}

const SEVERITY_TILE_STYLE: Record<string, string> = {
  critical: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
  high:     "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20",
  medium:   "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  low:      "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  info:     "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
};

export default function Alerts() {
  const [severity, setSeverity] = useState<Severity | "all">("all");
  const [rule, setRule] = useState<Rule | "all">("all");
  const [showTriaged, setShowTriaged] = useState(false);
  const [selected, setSelected] = useState<FlowAlert | null>(null);

  const summary = useAlertSummary(showTriaged ? undefined : false);
  const q = useAlerts({
    severity: severity === "all" ? undefined : severity,
    rule:     rule === "all"     ? undefined : rule,
    triaged:  showTriaged ? undefined : false,
    limit: 200,
  });

  const ruleCount = (r: Rule | "all") => {
    if (!summary.data) return 0;
    if (r === "all") return summary.data.total;
    return summary.data.by_rule.find((x) => x.rule === r)?.total ?? 0;
  };
  const ruleCritical = (r: Rule | "all") => {
    if (!summary.data) return 0;
    if (r === "all") return summary.data.by_severity.critical;
    return summary.data.by_rule.find((x) => x.rule === r)?.critical ?? 0;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Alerts</h1>
        <p className="text-sm text-muted-foreground">
          {showTriaged ? "All flow_alerts" : "Untriaged flow_alerts"}, sorted by
          severity (critical first) then most recent.
        </p>
      </div>

      {/* Severity-bucket summary tiles */}
      {summary.data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {(["critical", "high", "medium", "low", "info"] as Severity[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSeverity(severity === s ? "all" : s)}
              className={cn(
                "rounded-lg border px-4 py-3 text-left transition-shadow hover:shadow-sm",
                SEVERITY_TILE_STYLE[s],
                severity === s && "ring-2 ring-current",
              )}
            >
              <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
                {s}
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {summary.data.by_severity[s] ?? 0}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Rule tabs with counts + critical badge */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {RULE_TABS.map(({ rule: r, label }) => {
          const count = ruleCount(r);
          const crit = ruleCritical(r);
          const active = rule === r;
          return (
            <button
              key={r}
              type="button"
              onClick={() => setRule(r)}
              className={cn(
                "flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors",
                active
                  ? "border-primary bg-primary/5 text-foreground"
                  : "border-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              <span className="font-medium">{label}</span>
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                {count}
              </span>
              {crit > 0 && (
                <span className="rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-red-600 dark:text-red-400">
                  {crit} crit
                </span>
              )}
            </button>
          );
        })}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">View</span>
          <FilterPill label="untriaged" active={!showTriaged} onClick={() => setShowTriaged(false)} />
          <FilterPill label="all"        active={showTriaged}  onClick={() => setShowTriaged(true)} />
        </div>
      </div>

      {/* Severity quick-filter row (mirrors the tile selection above; keeping
          for keyboard / power-user use). Hidden on small screens to avoid clutter. */}
      <div className="hidden flex-wrap items-center gap-2 md:flex">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">Severity</span>
        {SEVERITIES.map((s) => (
          <FilterPill
            key={s}
            label={s}
            active={severity === s}
            onClick={() => setSeverity(s)}
          />
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Severity</th>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Rule</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Summary</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {q.isError && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-red-500">
                  Failed to load alerts.
                </td>
              </tr>
            )}
            {q.data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No alerts match the current filters.
                </td>
              </tr>
            )}
            {q.data?.items.map((a: FlowAlert) => (
              <tr
                key={a.id}
                onClick={() => setSelected(a)}
                className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-muted/40"
              >
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
                      SEVERITY_STYLES[a.severity],
                    )}
                  >
                    {a.severity}
                  </span>
                </td>
                <td className="px-4 py-3 tabular-nums text-muted-foreground">
                  {fmtTime(a.detected_at)}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{a.rule}</td>
                <td className="px-4 py-3 font-mono text-xs">{a.src_addr}</td>
                <td className="px-4 py-3 text-foreground">{a.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <AlertDetailDrawer
        alert={selected}
        open={selected !== null}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}
